import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "/logo-shield.png";

const Logo = () => (
  <a href="#home" className="flex items-center gap-3 select-none">
    <img
      src={LOGO_URL}
      alt="Pezente Digital"
      className="w-11 h-11 object-contain drop-shadow-[0_2px_12px_rgba(212,175,55,0.45)]"
    />
    <div className="flex flex-col leading-tight">
      <span className="font-serif text-xl light-text tracking-wide">PEZENTE</span>
      <span className="text-[10px] tracking-[0.35em] text-[#D4AF37] font-semibold">DIGITAL</span>
    </div>
  </a>
);

const LangSwitcher = ({ inline = false }) => {
  const { lang, setLang } = useLanguage();
  const langs = [
    { code: 'en', label: 'EN' },
    { code: 'pt', label: 'PT' },
    { code: 'it', label: 'IT' },
  ];
  return (
    <div className={`flex items-center ${inline ? 'gap-2' : 'gap-1'}`}>
      <Globe className="w-4 h-4 text-[#D4AF37] mr-1" />
      {langs.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`px-2.5 py-1 text-xs font-semibold tracking-widest rounded transition-all duration-200 ${
            lang === l.code
              ? 'bg-[rgba(212,175,55,0.15)] text-[#D4AF37] border border-[rgba(212,175,55,0.45)]'
              : 'text-[#9CA3AF] border border-transparent hover:text-[#D4AF37] hover:border-[rgba(212,175,55,0.25)]'
          }`}
          aria-label={`Change language to ${l.label}`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
};

const Navbar = () => {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[rgba(11,19,33,0.85)] backdrop-blur-md border-b border-[rgba(212,175,55,0.08)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-10">
          <button onClick={() => scrollTo('home')} className="text-xs tracking-[0.3em] text-[#E8EAF0] hover:text-[#D4AF37] transition-colors duration-200">
            {t.nav.home}
          </button>
          <button onClick={() => scrollTo('about')} className="text-xs tracking-[0.3em] text-[#E8EAF0] hover:text-[#D4AF37] transition-colors duration-200">
            {t.nav.about}
          </button>
          <button onClick={() => scrollTo('contact')} className="text-xs tracking-[0.3em] text-[#E8EAF0] hover:text-[#D4AF37] transition-colors duration-200">
            {t.nav.contact}
          </button>
          <LangSwitcher />
        </nav>

        <div className="hidden lg:block">
          <a
            href="mailto:IAM@pezente.com?subject=Consultation%20Request"
            className="gold-btn px-6 py-3 text-xs tracking-[0.25em] font-semibold rounded-md inline-block"
          >
            {t.nav.cta}
          </a>
        </div>

        <button className="lg:hidden text-[#D4AF37]" onClick={() => setOpen(!open)} aria-label="toggle menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B1321] border-t border-[rgba(212,175,55,0.1)]">
          <div className="px-6 py-6 flex flex-col gap-5">
            <button onClick={() => scrollTo('home')} className="text-left text-xs tracking-[0.3em] text-[#E8EAF0]">{t.nav.home}</button>
            <button onClick={() => scrollTo('about')} className="text-left text-xs tracking-[0.3em] text-[#E8EAF0]">{t.nav.about}</button>
            <button onClick={() => scrollTo('contact')} className="text-left text-xs tracking-[0.3em] text-[#E8EAF0]">{t.nav.contact}</button>
            <LangSwitcher inline />
            <a href="mailto:IAM@pezente.com?subject=Consultation%20Request" onClick={() => setOpen(false)} className="gold-btn px-6 py-3 text-xs tracking-[0.25em] font-semibold rounded-md text-center">
              {t.nav.cta}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
